import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import * as tokenStore from '../services/tokenStore';
import { AlertCircle, ArrowLeft, ArrowRight, Mail, MessageCircle, RefreshCw, ShieldCheck, KeyRound, CheckCircle2 } from 'lucide-react';
import logoLogin from '../logo_login.png';

// Where to go after login when a flow sent the user here: the AI-connector
// consent page, or a page a protected route / a ChatGPT link pointed at
// (backend/utils/mcp/privacy.js APP_ROUTES). Only these same-origin, relative
// app paths are honoured — never an arbitrary URL — so a crafted ?next=
// cannot turn the login page into an open redirect.
const ID_SEG = '[A-Za-z0-9_-]{1,128}';
const NEXT_PATHS = [
  '/connect-claude',
  '/dashboard',
  '/reports',
  '/students',
  `/groups/${ID_SEG}`,
  '/jibaya',
  '/lessons',
  `/lessons/${ID_SEG}/attendance`,
  '/teacher/attendance',
  '/teacher/dashboard',
  '/automation/leads',
  '/sport/payroll',
  `/tournaments/${ID_SEG}`,
].map((p) => new RegExp(`^${p}$`));
const NEXT_BASE = 'https://next.invalid';

export function safeNextPath(next) {
  if (typeof next !== 'string' || next.length > 2048) return null;
  // One leading slash; no protocol-relative "//", backslashes or control chars.
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) return null;
  for (let i = 0; i < next.length; i += 1) {
    const c = next.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return null;
  }
  let url;
  try {
    url = new URL(next, NEXT_BASE);
  } catch {
    return null;
  }
  if (url.origin !== NEXT_BASE) return null;
  if (!NEXT_PATHS.some((re) => re.test(url.pathname))) return null;
  return `${url.pathname}${url.search}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get('next'));
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  const { setUser, setLoading, isLoading, error, setError, token, user } = useAuthStore();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  // Login method (email = default, whatsapp = OTP via WhatsApp to worker + managers)
  const [loginMethod, setLoginMethod] = useState('email');
  const [whatsappDisabled, setWhatsappDisabled] = useState(false);
  const [whatsappDisabledReason, setWhatsappDisabledReason] = useState(null);

  // OTP state (shared between login OTP and forgot password OTP)
  const [otpStep, setOtpStep] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpChannel, setOtpChannel] = useState('email');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [managerNotifiedCount, setManagerNotifiedCount] = useState(0);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Forgot password state
  const [forgotStep, setForgotStep] = useState(false); // false | 'username' | 'otp' | 'newPassword' | 'success'
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotOtpToken, setForgotOtpToken] = useState('');
  const [forgotMaskedEmail, setForgotMaskedEmail] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotOtpDigits, setForgotOtpDigits] = useState(['', '', '', '', '', '']);
  const [forgotCountdown, setForgotCountdown] = useState(0);
  const [forgotResending, setForgotResending] = useState(false);
  const forgotInputRefs = useRef([]);

  // Countdown timer for login OTP resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Countdown timer for forgot password OTP resend
  useEffect(() => {
    if (forgotCountdown <= 0) return;
    const timer = setTimeout(() => setForgotCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [forgotCountdown]);

  // Helper: determine correct dashboard route based on role and center type
  const getRedirectPath = (u, ct) => {
    if (nextPath) return nextPath;
    if (u?.role === 'teacher') {
      return ct === 'hair_transplant' ? '/worker/dashboard' : '/teacher/dashboard';
    }
    return '/dashboard';
  };

  // If already authenticated, redirect to dashboard
  if (token) {
    const target = getRedirectPath(user, user?.centerType || tokenStore.getItem('centerType'));
    return <Navigate to={target} replace />;
  }

  // ========== LOGIN HANDLERS ==========
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!credentials.username.trim()) {
      setError(t('validation.required'));
      return;
    }

    if (!credentials.password.trim()) {
      setError(t('validation.required'));
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/login', { ...credentials, method: loginMethod });

      if (response.requiresOTP) {
        setOtpToken(response.otpToken);
        const channel = response.channel || 'email';
        setOtpChannel(channel);
        if (channel === 'whatsapp') {
          setMaskedPhone(response.maskedPhone || '');
          setManagerNotifiedCount(response.managerNotified || 0);
        } else {
          setMaskedEmail(response.email || '');
        }
        setOtpStep(true);
        setOtpDigits(['', '', '', '', '', '']);
        setCountdown(60);
        setError(null);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        setUser(response.user, response.token);
        navigate(getRedirectPath(response.user, response.user?.centerType));
      }
    } catch (err) {
      // If WhatsApp isn't usable for this user, disable the pill for the
      // rest of the session and silently switch them to email so they can
      // re-submit without rebuilding their password.
      if (err?.code === 'NO_PHONE_ON_FILE' || err?.code === 'WA_NOT_CONFIGURED') {
        setWhatsappDisabled(true);
        setWhatsappDisabledReason(err.code);
        setLoginMethod('email');
      }
      let errorMessage = t('login.invalidCredentials');
      if (err?.message) errorMessage = err.message;
      else if (err?.error) errorMessage = err.error;
      else if (typeof err === 'string') errorMessage = err;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ========== LOGIN OTP HANDLERS ==========
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5 && newDigits.every(d => d !== '')) {
      handleOtpSubmit(newDigits.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setOtpDigits(newDigits);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
    if (pasted.length === 6) {
      handleOtpSubmit(pasted);
    }
  };

  const handleOtpSubmit = async (codeStr) => {
    const code = codeStr || otpDigits.join('');
    if (code.length !== 6) {
      setError(t('login.otpRequired'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-login-otp', {
        otpToken,
        code
      });

      setUser(response.user, response.token);
      navigate(getRedirectPath(response.user, response.user?.centerType));
    } catch (err) {
      let errorMessage = t('login.otpInvalid');
      if (err?.message) errorMessage = err.message;
      else if (err?.error) errorMessage = err.error;

      if (err?.expired) {
        setOtpStep(false);
        setOtpToken('');
        setOtpDigits(['', '', '', '', '', '']);
      } else {
        setOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0 || resending) return;
    setResending(true);
    setError(null);

    try {
      const response = await api.post('/auth/resend-login-otp', { otpToken });
      setOtpToken(response.otpToken);
      const channel = response.channel || otpChannel || 'email';
      setOtpChannel(channel);
      if (channel === 'whatsapp') {
        setMaskedPhone(response.maskedPhone || '');
        setManagerNotifiedCount(response.managerNotified || 0);
      } else {
        setMaskedEmail(response.email || '');
      }
      setOtpDigits(['', '', '', '', '', '']);
      setCountdown(60);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      let errorMessage = t('login.otpResendFailed');
      if (err?.message) errorMessage = err.message;
      if (err?.expired) {
        setOtpStep(false);
        setOtpToken('');
      }
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setOtpToken('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpChannel('email');
    setMaskedPhone('');
    setManagerNotifiedCount(0);
    setError(null);
    // Intentionally NOT resetting whatsappDisabled — once we know there's
    // no phone for this user, keep the pill disabled for the session.
  };

  // ========== FORGOT PASSWORD HANDLERS ==========
  const handleForgotPasswordClick = () => {
    setForgotStep('username');
    setForgotUsername('');
    setError(null);
  };

  const handleBackFromForgot = () => {
    setForgotStep(false);
    setForgotUsername('');
    setForgotOtpToken('');
    setForgotMaskedEmail('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotOtpDigits(['', '', '', '', '', '']);
    setForgotCountdown(0);
    setError(null);
  };

  const handleForgotSubmitUsername = async (e) => {
    e.preventDefault();
    setError(null);

    if (!forgotUsername.trim()) {
      setError(t('validation.required'));
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { username: forgotUsername.trim() });
      setForgotOtpToken(response.otpToken);
      setForgotMaskedEmail(response.email);
      setForgotStep('otp');
      setForgotOtpDigits(['', '', '', '', '', '']);
      setForgotCountdown(60);
      setError(null);
      setTimeout(() => forgotInputRefs.current[0]?.focus(), 100);
    } catch (err) {
      let errorMessage = t('login.noEmailError');
      if (err?.message) errorMessage = err.message;
      else if (err?.error) errorMessage = err.error;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newDigits = [...forgotOtpDigits];
    newDigits[index] = value;
    setForgotOtpDigits(newDigits);

    if (value && index < 5) {
      forgotInputRefs.current[index + 1]?.focus();
    }

    if (value && index === 5 && newDigits.every(d => d !== '')) {
      handleForgotOtpVerify(newDigits.join(''));
    }
  };

  const handleForgotOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !forgotOtpDigits[index] && index > 0) {
      forgotInputRefs.current[index - 1]?.focus();
    }
  };

  const handleForgotOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const newDigits = [...forgotOtpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setForgotOtpDigits(newDigits);
    const focusIdx = Math.min(pasted.length, 5);
    forgotInputRefs.current[focusIdx]?.focus();
    if (pasted.length === 6) {
      handleForgotOtpVerify(pasted);
    }
  };

  const handleForgotOtpVerify = async (codeStr) => {
    const code = codeStr || forgotOtpDigits.join('');
    if (code.length !== 6) {
      setError(t('login.otpRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Verify the code with the backend before moving on, so an invalid
      // or expired code is surfaced on the OTP screen (not on the new
      // password screen, which was very confusing for users).
      await api.post('/auth/verify-reset-code', {
        otpToken: forgotOtpToken,
        code
      });

      // Store the code for the subsequent reset-password call
      setForgotOtpDigits(code.split(''));
      setForgotStep('newPassword');
      setError(null);
    } catch (err) {
      let errorMessage = t('login.otpInvalid');
      if (err?.message) errorMessage = err.message;
      else if (err?.error) errorMessage = err.error;

      // If the session / code has fully expired, send the user back to the
      // username step so they can request a fresh code.
      if (err?.expired) {
        setForgotStep('username');
        setForgotOtpToken('');
        setForgotOtpDigits(['', '', '', '', '', '']);
      } else {
        // Clear the digits so the user can retype — keeps them on the OTP step.
        setForgotOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => forgotInputRefs.current[0]?.focus(), 0);
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotResendOtp = async () => {
    if (forgotCountdown > 0 || forgotResending) return;
    setForgotResending(true);
    setError(null);

    try {
      const response = await api.post('/auth/forgot-password', { username: forgotUsername.trim() });
      setForgotOtpToken(response.otpToken);
      setForgotMaskedEmail(response.email);
      setForgotOtpDigits(['', '', '', '', '', '']);
      setForgotCountdown(60);
      setTimeout(() => forgotInputRefs.current[0]?.focus(), 100);
    } catch (err) {
      let errorMessage = t('login.otpResendFailed');
      if (err?.message) errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setForgotResending(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (!forgotNewPassword || !forgotConfirmPassword) {
      setError(t('validation.required'));
      return;
    }

    if (forgotNewPassword !== forgotConfirmPassword) {
      setError(t('validation.passwordMismatch'));
      return;
    }

    if (forgotNewPassword.length < 6) {
      setError(t('login.passwordMinLength'));
      return;
    }

    if (isLoading) return; // guard against double-submit

    setLoading(true);

    try {
      // Use noRetry so a transient backend error doesn't cause axios to
      // auto-retry this POST — retries would race with the in-flight write
      // and could surface misleading errors like "Reset code not found".
      await api.noRetry.post('/auth/reset-password', {
        otpToken: forgotOtpToken,
        code: forgotOtpDigits.join(''),
        newPassword: forgotNewPassword
      });

      setForgotStep('success');
      setError(null);
    } catch (err) {
      let errorMessage = t('login.otpInvalid');
      if (err?.message) errorMessage = err.message;
      else if (err?.error) errorMessage = err.error;

      if (err?.expired) {
        // Session / code fully expired — restart from the beginning.
        setForgotStep('username');
        setForgotOtpToken('');
        setForgotOtpDigits(['', '', '', '', '', '']);
      } else if (err?.attemptsRemaining !== undefined) {
        // Code is invalid — send the user back to the OTP entry screen so
        // the error lands on the right field instead of the password form.
        setForgotOtpDigits(['', '', '', '', '', '']);
        setForgotNewPassword('');
        setForgotConfirmPassword('');
        setForgotStep('otp');
        setTimeout(() => forgotInputRefs.current[0]?.focus(), 0);
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;
  const isOtpDisabled = isLoading || otpDigits.some(d => !d);
  const isForgotOtpDisabled = isLoading || forgotOtpDigits.some(d => !d);

  // ========== SHARED STYLES ==========
  const backBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    color: 'var(--color-text-secondary, #6B7280)',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '1.25rem',
    fontSize: '0.8125rem',
    fontWeight: '500',
    transition: 'opacity 0.2s'
  };

  const primaryBtnStyle = (disabled) => ({
    width: '100%',
    height: '50px',
    fontSize: '0.9375rem',
    fontWeight: '700',
    borderRadius: '10px',
    color: 'white',
    backgroundColor: disabled ? '#D1D5DB' : '#10B981',
    border: 'none',
    boxShadow: disabled ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.2s ease',
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  const inputStyle = {
    height: '52px',
    fontSize: '0.9375rem',
    padding: '12px 16px',
    border: '1.5px solid #D1D5DB',
    borderRadius: '10px',
    direction: isRTL ? 'rtl' : 'ltr',
    transition: 'border-color 0.2s ease'
  };

  const labelStyle = {
    display: 'block',
    color: 'var(--color-text-secondary, #374151)',
    fontWeight: '600',
    marginBottom: '6px',
    fontSize: '0.875rem',
    textAlign: isRTL ? 'right' : 'left'
  };

  const loadingSpinner = (
    <span style={{
      display: 'inline-block',
      width: '16px',
      height: '16px',
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
  );

  // ========== RENDER OTP INPUTS (reusable) ==========
  const renderOtpInputs = (digits, refs, onChange, onKeyDown, onPaste) => (
    <div style={{
      display: 'flex',
      gap: '10px',
      justifyContent: 'center',
      marginBottom: '1.75rem',
      direction: 'ltr'
    }}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={el => refs.current[idx] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => onChange(idx, e.target.value)}
          onKeyDown={(e) => onKeyDown(idx, e)}
          onPaste={idx === 0 ? onPaste : undefined}
          className="otp-input"
          placeholder=""
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );

  // ========== DETERMINE WHICH VIEW TO SHOW ==========
  const renderContent = () => {
    // Forgot password flow
    if (forgotStep === 'username') {
      return (
        <div style={{ animation: 'slideInUp 0.4s ease-out' }}>
          <button className="back-btn" onClick={handleBackFromForgot} style={backBtnStyle}>
            <BackArrow size={15} />
            {t('login.backToLogin')}
          </button>

          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <KeyRound size={28} color="#D97706" strokeWidth={2} />
            </div>
            <h2 style={{
              margin: '0 0 6px',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--color-text-primary, #111827)',
              letterSpacing: '-0.01em'
            }}>
              {t('login.forgotPasswordTitle')}
            </h2>
            <p style={{
              margin: '0',
              fontSize: '0.8125rem',
              color: 'var(--color-text-secondary, #6B7280)',
              lineHeight: '1.5'
            }}>
              {t('login.enterUsername')}
            </p>
          </div>

          <form onSubmit={handleForgotSubmitUsername} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>
                {t('login.username')}
              </label>
              <input
                type="text"
                value={forgotUsername}
                onChange={(e) => setForgotUsername(e.target.value)}
                className="input auth-input"
                placeholder={t('login.enterEmail')}
                required
                autoFocus
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={primaryBtnStyle(isLoading)}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {loadingSpinner}
                  {t('login.sending')}
                </div>
              ) : (
                t('login.sendResetCode')
              )}
            </button>
          </form>
        </div>
      );
    }

    if (forgotStep === 'otp') {
      return (
        <div style={{ animation: 'slideInUp 0.4s ease-out' }}>
          <button className="back-btn" onClick={() => { setForgotStep('username'); setError(null); }} style={backBtnStyle}>
            <BackArrow size={15} />
            {t('login.backToForgotPassword')}
          </button>

          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <ShieldCheck size={28} color="#D97706" strokeWidth={2} />
            </div>
            <h2 style={{
              margin: '0 0 6px',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--color-text-primary, #111827)',
              letterSpacing: '-0.01em'
            }}>
              {t('login.otpTitle')}
            </h2>
            <p style={{
              margin: '0 0 10px',
              fontSize: '0.8125rem',
              color: 'var(--color-text-secondary, #6B7280)',
              lineHeight: '1.5'
            }}>
              {t('login.otpSentTo')}
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              backgroundColor: 'var(--color-warning-light, #FFFBEB)',
              border: '1px solid #FDE68A',
              borderRadius: '20px',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: '#D97706',
              direction: 'ltr'
            }}>
              <Mail size={13} />
              {forgotMaskedEmail}
            </div>
          </div>

          {renderOtpInputs(forgotOtpDigits, forgotInputRefs, handleForgotOtpChange, handleForgotOtpKeyDown, handleForgotOtpPaste)}

          <button
            onClick={() => handleForgotOtpVerify()}
            disabled={isForgotOtpDisabled}
            style={{ ...primaryBtnStyle(isForgotOtpDisabled), marginBottom: '1.25rem' }}
          >
            {t('login.otpVerify')}
          </button>

          <div style={{ textAlign: 'center' }}>
            {forgotCountdown > 0 ? (
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary, #9CA3AF)', fontWeight: '400' }}>
                {t('login.otpResendIn')} <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>{forgotCountdown}s</span>
              </span>
            ) : (
              <button
                className="resend-btn"
                onClick={handleForgotResendOtp}
                disabled={forgotResending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#D97706',
                  cursor: forgotResending ? 'not-allowed' : 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 8px'
                }}
              >
                <RefreshCw size={13} style={forgotResending ? { animation: 'spin 1s linear infinite' } : {}} />
                {t('login.otpResend')}
              </button>
            )}
          </div>
        </div>
      );
    }

    if (forgotStep === 'newPassword') {
      return (
        <div style={{ animation: 'slideInUp 0.4s ease-out' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <KeyRound size={28} color="#D97706" strokeWidth={2} />
            </div>
            <h2 style={{
              margin: '0 0 6px',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--color-text-primary, #111827)',
              letterSpacing: '-0.01em'
            }}>
              {t('login.resetPasswordTitle')}
            </h2>
          </div>

          <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={labelStyle}>
                {t('login.newPasswordLabel')}
              </label>
              <input
                type="password"
                value={forgotNewPassword}
                onChange={(e) => setForgotNewPassword(e.target.value)}
                className="input auth-input"
                placeholder={t('login.enterNewPassword')}
                required
                autoFocus
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>
                {t('login.confirmPasswordLabel')}
              </label>
              <input
                type="password"
                value={forgotConfirmPassword}
                onChange={(e) => setForgotConfirmPassword(e.target.value)}
                className="input auth-input"
                placeholder={t('login.confirmNewPassword')}
                required
                style={inputStyle}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{ ...primaryBtnStyle(isLoading), marginTop: '8px' }}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  {loadingSpinner}
                  {t('login.resetting')}
                </div>
              ) : (
                t('login.resetPassword')
              )}
            </button>
          </form>
        </div>
      );
    }

    if (forgotStep === 'success') {
      return (
        <div style={{ animation: 'slideInUp 0.4s ease-out', textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <CheckCircle2 size={28} color="#10B981" strokeWidth={2} />
          </div>
          <h2 style={{
            margin: '0 0 12px',
            fontSize: '1.25rem',
            fontWeight: '700',
            color: 'var(--color-text-primary, #111827)',
            letterSpacing: '-0.01em'
          }}>
            {t('login.resetPasswordTitle')}
          </h2>
          <p style={{
            margin: '0 0 24px',
            fontSize: '0.875rem',
            color: '#059669',
            lineHeight: '1.5',
            fontWeight: '500'
          }}>
            {t('login.passwordResetSuccess')}
          </p>
          <button
            onClick={handleBackFromForgot}
            style={primaryBtnStyle(false)}
          >
            {t('login.backToLogin')}
          </button>
        </div>
      );
    }

    // Login OTP step
    if (otpStep) {
      return (
        <div style={{ animation: 'slideInUp 0.4s ease-out' }}>
          <button className="back-btn" onClick={handleBackToLogin} style={backBtnStyle}>
            <BackArrow size={15} />
            {t('login.backToLogin')}
          </button>

          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <ShieldCheck size={28} color="#10B981" strokeWidth={2} />
            </div>
            <h2 style={{
              margin: '0 0 6px',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: 'var(--color-text-primary, #111827)',
              letterSpacing: '-0.01em'
            }}>
              {t('login.otpTitle')}
            </h2>
            <p style={{
              margin: '0 0 10px',
              fontSize: '0.8125rem',
              color: 'var(--color-text-secondary, #6B7280)',
              lineHeight: '1.5'
            }}>
              {t(otpChannel === 'whatsapp' ? 'login.otpSentToWhatsApp' : 'login.otpSentTo')}
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              backgroundColor: 'var(--color-success-light, #F0FDF4)',
              border: '1px solid #BBF7D0',
              borderRadius: '20px',
              fontSize: '0.8125rem',
              fontWeight: '600',
              color: '#059669',
              direction: 'ltr'
            }}>
              {otpChannel === 'whatsapp' ? <MessageCircle size={13} /> : <Mail size={13} />}
              {otpChannel === 'whatsapp' ? maskedPhone : maskedEmail}
            </div>
            {otpChannel === 'whatsapp' && managerNotifiedCount > 0 && (
              <div style={{
                marginTop: '8px',
                fontSize: '0.75rem',
                color: 'var(--color-text-secondary, #6B7280)',
                textAlign: 'center',
                lineHeight: '1.4'
              }}>
                {t('login.otpSentToManager', { count: managerNotifiedCount })}
              </div>
            )}
          </div>

          {renderOtpInputs(otpDigits, inputRefs, handleOtpChange, handleOtpKeyDown, handleOtpPaste)}

          <button
            onClick={() => handleOtpSubmit()}
            disabled={isOtpDisabled}
            style={{ ...primaryBtnStyle(isOtpDisabled), marginBottom: '1.25rem' }}
          >
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                {loadingSpinner}
                {t('login.otpVerifying')}
              </div>
            ) : (
              t('login.otpVerify')
            )}
          </button>

          <div style={{ textAlign: 'center' }}>
            {countdown > 0 ? (
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary, #9CA3AF)', fontWeight: '400' }}>
                {t('login.otpResendIn')} <span style={{ fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>{countdown}s</span>
              </span>
            ) : (
              <button
                className="resend-btn"
                onClick={handleResendOtp}
                disabled={resending}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#10B981',
                  cursor: resending ? 'not-allowed' : 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 8px'
                }}
              >
                <RefreshCw size={13} style={resending ? { animation: 'spin 1s linear infinite' } : {}} />
                {t('login.otpResend')}
              </button>
            )}
          </div>
        </div>
      );
    }

    // Default: Login form
    const methodPillStyle = (active, disabled = false) => ({
      flex: 1,
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.8125rem',
      fontWeight: 600,
      backgroundColor: active ? 'var(--color-surface, #FFFFFF)' : 'transparent',
      color: disabled ? 'var(--color-text-tertiary, #9CA3AF)' : (active ? '#10B981' : 'var(--color-text-secondary, #6B7280)'),
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
      transition: 'all 0.15s'
    });
    const waTooltip = whatsappDisabled
      ? t(whatsappDisabledReason === 'NO_PHONE_ON_FILE' ? 'login.noPhoneError' : 'login.waNotConfigured')
      : '';

    return (
      <form className="auth-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div
          role="radiogroup"
          aria-label={t('login.chooseMethod')}
          style={{
            display: 'flex',
            gap: '8px',
            padding: '4px',
            backgroundColor: 'var(--color-surface-alt, #F3F4F6)',
            borderRadius: '10px'
          }}
        >
          <button
            type="button"
            role="radio"
            aria-checked={loginMethod === 'email'}
            onClick={() => setLoginMethod('email')}
            style={methodPillStyle(loginMethod === 'email')}
          >
            <Mail size={14} />
            {t('login.methodEmail')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={loginMethod === 'whatsapp'}
            onClick={() => !whatsappDisabled && setLoginMethod('whatsapp')}
            disabled={whatsappDisabled}
            title={waTooltip}
            style={methodPillStyle(loginMethod === 'whatsapp', whatsappDisabled)}
          >
            <MessageCircle size={14} />
            {t('login.methodWhatsApp')}
          </button>
        </div>

        <div>
          <label style={labelStyle}>
            {t('login.username')}
          </label>
          <input
            type="text"
            name="username"
            value={credentials.username}
            onChange={handleChange}
            className="input auth-input"
            placeholder={t('login.enterEmail')}
            required
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>
            {t('login.password')}
          </label>
          <input
            type="password"
            name="password"
            value={credentials.password}
            onChange={handleChange}
            className="input auth-input"
            placeholder={t('login.enterPassword')}
            required
            style={inputStyle}
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary auth-button"
          style={{
            width: '100%',
            height: '50px',
            marginTop: '8px',
            fontSize: '0.9375rem',
            fontWeight: '700',
            borderRadius: '10px',
            color: 'white',
            backgroundColor: isLoading ? '#9CA3AF' : '#10B981',
            border: 'none',
            boxShadow: isLoading ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.2s ease',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              {loadingSpinner}
              {t('common.loading')}
            </div>
          ) : (
            t('login.login')
          )}
        </button>

        {/* Forgot Password Link */}
        <div style={{ textAlign: 'center', marginTop: '4px' }}>
          <button
            type="button"
            onClick={handleForgotPasswordClick}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary, #6B7280)',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: '500',
              padding: '4px 8px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#10B981'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
          >
            {t('login.forgotPassword')}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="auth-container" style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 'var(--mobile-padding-screen, 1rem)',
      direction: isRTL ? 'rtl' : 'ltr',
      position: 'relative'
    }}>
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }

        .login-card {
          animation: slideInUp 0.5s ease-out;
        }

        .auth-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 200px;
          background: linear-gradient(135deg, var(--mobile-primary, #10B981) 0%, var(--mobile-primary-hover, #059669) 100%);
          border-radius: 0 0 50% 50%;
          z-index: 0;
        }

        .login-card {
          position: relative;
          z-index: 1;
        }

        .otp-input {
          width: 50px;
          height: 58px;
          text-align: center;
          font-size: 22px;
          font-weight: 700;
          border: 2px solid var(--color-border-dark, #D1D5DB);
          border-radius: 12px;
          outline: none;
          transition: all 0.2s ease;
          background: var(--color-surface-alt, #FAFAFA);
          color: var(--color-text-primary, #111827);
          caret-color: #10B981;
        }

        .otp-input:focus {
          border-color: #10B981;
          background: var(--color-surface, white);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12);
        }

        .otp-input:not(:placeholder-shown) {
          border-color: #10B981;
          background: var(--color-surface, white);
        }

        .error-alert {
          animation: slideInUp 0.3s ease-out, shake 0.4s ease-out 0.1s;
        }

        .back-btn:hover {
          opacity: 0.8;
        }

        .resend-btn:hover {
          text-decoration: underline;
        }
      `}</style>

      <div className="login-card" style={{
        backgroundColor: 'var(--color-surface, white)',
        width: '100%',
        maxWidth: '420px',
        margin: '0 auto',
        padding: '2.5rem 2rem 2rem',
        borderRadius: '1.5rem',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)'
      }}>
        {/* Logo/Header Section */}
        <div style={{ marginBottom: '4px', textAlign: 'center' }}>
          <img
            src={logoLogin}
            alt={t('header.appLogoAlt')}
            className="auth-logo"
            style={{
              width: '120px',
              height: 'auto',
              margin: '0 auto',
              display: 'block',
              objectFit: 'contain'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div style={{
            width: '120px',
            height: '120px',
            background: 'linear-gradient(135deg, var(--mobile-primary, #10B981) 0%, var(--mobile-primary-hover, #059669) 100%)',
            borderRadius: '1rem',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)'
          }}>
            <span style={{ fontSize: '3rem' }}>📚</span>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            backgroundColor: 'var(--color-danger-light, #FEF2F2)',
            border: '1px solid #FECACA',
            borderRadius: '0.75rem',
            padding: '0.875rem 1rem',
            marginBottom: '1.25rem'
          }}>
            <AlertCircle style={{
              width: '1.125rem',
              height: '1.125rem',
              color: '#DC2626',
              flexShrink: 0
            }} />
            <span style={{
              color: 'var(--color-danger-dark, #991B1B)',
              fontSize: '0.8125rem',
              fontWeight: '500',
              lineHeight: '1.4'
            }}>
              {error}
            </span>
          </div>
        )}

        {renderContent()}

        {/* Help Link */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <a
            href="/help"
            style={{
              color: '#4FD1C5',
              fontSize: '0.85rem',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            {t('common.help')}
          </a>
        </div>

        {/* Footer Text */}
        <p style={{
          color: '#C4C4C4',
          fontSize: '0.75rem',
          textAlign: 'center',
          marginTop: '1rem',
          fontWeight: '400',
          letterSpacing: '0.02em'
        }}>
          {t('login.designedBy')}
        </p>
      </div>
    </div>
  );
}
